<template>
  <div class="auth-container" :class="{ 'theme-light': !isDarkTheme }">
    <button type="button" class="btn-back" @click="goBack" aria-label="Voltar">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M19 12H5"/><path d="M12 19l-7-7 7-7"/></svg>
      <span>Voltar</span>
    </button>
    <div class="auth-left" ref="scrollArea">
      <div class="auth-wrapper">
        <div class="auth-content">
          <!-- Logo e Branding -->
          <div class="auth-header">
            <div class="logo-container">
              <a href="/" class="logo-link">
                <img class="auth-logo" src="/img/logo-importmaisbrasil-stacked.png" alt="Import Mais Brasil" />
              </a>
            </div>
            <p class="brand-subtitle">Acesse sua conta</p>
          </div>

          <!-- Formulário de Login -->
          <form @submit.prevent="onSubmit" class="auth-form">
            <div class="form-group">
              <label for="email" class="form-label">E-mail</label>
              <div class="input-wrapper">
                <input 
                  id="email"
                  type="email" 
                  v-model="email" 
                  placeholder="seu@email.com" 
                  class="form-input"
                  required 
                />
                <span class="input-icon"><AuthIcon name="mail" /></span>
              </div>
            </div>

            <div class="form-group">
              <label for="password" class="form-label">Senha</label>
              <div class="input-wrapper">
                <input 
                  id="password"
                  :type="show ? 'text' : 'password'" 
                  v-model="password" 
                  placeholder="••••••••" 
                  class="form-input"
                  required 
                />
                <span class="input-icon"><AuthIcon name="lock" /></span>
                <button 
                  type="button" 
                  class="toggle-password"
                  @click="show = !show"
                  :title="show ? 'Ocultar senha' : 'Mostrar senha'"
                  :aria-label="show ? 'Ocultar senha' : 'Mostrar senha'"
                >
                  <AuthIcon :name="show ? 'eye-off' : 'eye'" />
                </button>
              </div>
            </div>

            <!-- Placeholder para Recaptcha -->
            <div class="recaptcha-placeholder" v-if="recaptchaVisible">
              <div class="g-recaptcha" data-sitekey="SUA_SITE_KEY"></div>
            </div>

            <!-- Mensagem de Erro -->
            <div v-if="msg" class="alert alert-error">
              <span class="alert-icon"><AuthIcon name="alert" :size="18" /></span>
              <span class="alert-text">{{ msg }}</span>
            </div>

            <!-- Botão de Login -->
            <button 
              type="submit" 
              class="btn-login"
              :disabled="loading"
            >
              <span v-if="!loading" class="btn-text">Entrar na Conta</span>
              <span v-else class="btn-text loading">
                <span class="spinner"></span> Conectando...
              </span>
            </button>
          </form>

          <!-- Links Adicionais -->
          <div class="auth-footer">
            <router-link :to="{ path: '/recovery' }" class="link-forgot">Esqueci minha senha</router-link>
            <span class="divider">•</span>
            <router-link :to="{ path: '/register', query: redirectQuery }" class="link-register">Criar conta</router-link>
          </div>

          <!-- Login Social -->
          <div class="social-login-container">
            <GoogleLogin class="google-btn-custom"/>
          </div>
        </div>
      </div>
    </div>

    <!-- Lado Direito - Imagem -->
    <div class="auth-right" @wheel.prevent="forwardWheel">
      <div class="bg-image"></div>
      <div class="overlay-gradient"></div>
    </div>
  </div>
</template>

<script>
import AuthIcon from "@/components/AuthIcon.vue";
import GoogleLogin from "@/components/GoogleLogin.vue"
export default {
  name: "Login",
  components: {
    GoogleLogin,
    AuthIcon
  },
  data() {
    return {
      msg: '',
      email: "",
      password: "",
      show: false,
      loading: false,
      prevPath: "",
      recaptchaVisible: true,
      // Reflete o tema salvo (mesmo padrão usado em Logo.vue), para que
      // esta tela acompanhe o tema claro/escuro escolhido pelo usuário -
      // o botão de alternância em si não aparece mais aqui, apenas no
      // menu da conta (AccountMenu), aberto pelo botão de login do
      // cabeçalho.
      isDarkTheme: localStorage.getItem("siteTheme") !== "light",
    };
  },
  beforeRouteEnter(to, from, next) {
    next((vm) => {
      vm.prevPath = from && from.name ? from.path : "";
    });
  },
  computed: {
    // Caminho interno (?redirect=) para onde voltar/continuar. Só aceita caminhos do próprio
    // site (começam com uma única "/") e nunca /login ou /register (evita laços).
    redirectSeguro() {
      const r = this.$route && this.$route.query ? this.$route.query.redirect : "";
      if (typeof r !== "string" || r.charAt(0) !== "/" || r.charAt(1) === "/" || r.charAt(1) === "\\") return "";
      if (/^\/(login|register)([\/?]|$)/.test(r)) return "";
      return r;
    },
    redirectQuery() {
      return this.redirectSeguro ? { redirect: this.redirectSeguro } : {};
    },
  },
  created() {
    this.$root.$on("mx3-theme-changed", this.onThemeChanged);
  },
  beforeDestroy() {
    this.$root.$off("mx3-theme-changed", this.onThemeChanged);
  },
  methods: {
    // Botão Voltar: volta à página anterior da loja; se a tela foi aberta direto
    // (sem histórico interno) leva para a página inicial.
    goBack() {
      // 1) Veio de uma ação que exigia login (carrinho, avaliação, lista de desejos...):
      //    volta para a tela de onde saiu (guardada em ?redirect=).
      const origem = this.redirectSeguro;
      if (origem) {
        // Se a tela de origem é justamente a anterior no histórico, volta nela sem empilhar outra entrada.
        if (this.prevPath && this.prevPath === origem.split("?")[0]) {
          this.$router.back();
          return;
        }
        const rr = this.$router.push(origem);
        if (rr && typeof rr.catch === "function") rr.catch(() => {});
        return;
      }
      // 2) Navegação normal: volta à tela anterior, exceto cadastro recém-concluído e
      //    telas que apenas redirecionam para o login (pagamento) - nesses casos, início.
      const voltar = this.prevPath && !["/login", "/register", "/payment", "/recovery"].includes(this.prevPath);
      if (voltar) {
        this.$router.back();
        return;
      }
      const r = this.$router.push("/");
      if (r && typeof r.catch === "function") r.catch(() => {});
    },
    // A imagem é estática: a roda do mouse sobre ela rola o formulário.
    forwardWheel(e) {
      const el = this.$refs.scrollArea;
      if (el) el.scrollTop += e.deltaY;
    },
    onThemeChanged(isDark) {
      this.isDarkTheme = isDark;
    },
    async onSubmit() {
      this.loading = true;
      this.msg = "";
      try {
        const bodyContent = JSON.stringify({
          username: this.email,
          password: this.password,
        });

        const response = await fetch(window.location.origin + "/api/login.php", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Accept": "*/*",
          },
          body: bodyContent,
        });

        let data;
        const contentType = response.headers.get("content-type");
        if (contentType && contentType.indexOf("application/json") !== -1) {
            data = await response.json();
        } else {
            const text = await response.text();
            throw new Error("Resposta inválida do servidor: " + text.substring(0, 50));
        }

        if (!response.ok) {
          const erro = new Error(data.detail || data.message || data.error || data.erro || "Erro ao fazer login");
          erro.status = response.status;
          throw erro;
        }

        localStorage.setItem("accessToken", data.access || data.token || "logged_in");
        await this.$api.getAddon(this.redirectSeguro || "/");
        
      } catch (err) {
        // Mostra o motivo real quando o servidor o informa (senha incorreta,
        // excesso de tentativas etc.) em vez de uma mensagem genérica única.
        if (err.status === 401) {
          this.msg = "E-mail ou senha incorretos. Se ainda não tem conta na loja, clique em \"Criar conta\" (o acesso do Gerenciador não vale para clientes).";
        } else if (err.status === 429) {
          this.msg = err.message || "Muitas tentativas. Aguarde alguns minutos e tente novamente.";
        } else if (err.status) {
          this.msg = err.message || "Houve um problema ao realizar login. Tente novamente.";
        } else {
          this.msg = "Houve um problema ao realizar login. Verifique sua conexão e tente novamente.";
        }
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.auth-container {
  display: grid;
  grid-template-columns: 1fr 1fr;
  min-height: 100vh;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
  position: relative;
}

.auth-left {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  background: linear-gradient(135deg, #151b27 0%, #1c232f 50%, #1c1e20 100%);
  position: relative;
  overflow: hidden;
}

.auth-left::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: 
    radial-gradient(circle at 20% 50%, rgba(255, 102, 0, 0.08) 0%, transparent 50%),
    radial-gradient(circle at 80% 80%, rgba(204, 82, 0, 0.06) 0%, transparent 50%);
  pointer-events: none;
}

.auth-wrapper {
  position: relative;
  z-index: 2;
  width: 100%;
  max-width: 400px;
  background: rgba(29, 38, 54, 0.6);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 102, 0, 0.15);
  border-radius: 20px;
  padding: 32px 36px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
}

.auth-content {
  display: flex;
  flex-direction: column;
}

.auth-header {
  text-align: center;
  margin-bottom: 24px;
}

.logo-container {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
}

/* Logomarca completa (globo + texto). Imagem própria, independente das
   regras globais de .logo-site-img do cabeçalho. */
.auth-logo {
  display: block;
  width: 170px;
  max-width: 100%;
  height: auto;
  object-fit: contain;
}

.auth-container:not(.theme-light) .auth-logo {
  filter: drop-shadow(0 0 6px rgba(255, 255, 255, 0.35));
}

.logo-link {
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}



.brand-subtitle {
  font-size: 13px;
  color: #8a98b0;
  font-weight: 400;
}

.auth-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 12px; /* Reduzido conforme solicitado */
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.form-label {
  font-size: 11px;
  font-weight: 700;
  color: #b0b8cc;
  text-transform: uppercase;
  letter-spacing: 0.8px;
}

.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.form-input {
  width: 100%;
  padding: 10px 40px 10px 36px;
  background: linear-gradient(135deg, rgba(34, 45, 63, 0.5) 0%, rgba(29, 38, 54, 0.5) 100%);
  border: 1px solid rgba(255, 102, 0, 0.2);
  border-radius: 8px;
  color: #ffffff;
  font-size: 14px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  outline: none;
  backdrop-filter: blur(10px);
}

.form-input:focus {
  border-color: rgba(255, 102, 0, 0.6);
  box-shadow: 0 0 0 3px rgba(255, 102, 0, 0.15);
}

.input-icon {
  position: absolute;
  left: 10px;
  color: #6b788f;
  font-size: 14px;
  pointer-events: none;
}

.toggle-password {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #6b788f;
  cursor: pointer;
  font-size: 14px;
  padding: 4px 6px;
  z-index: 10;
}

.btn-login {
  width: 100%;
  padding: 10px 16px;
  background: linear-gradient(135deg, #ff6600 0%, #cc5200 100%);
  border: 1px solid rgba(255, 102, 0, 0.3);
  border-radius: 8px;
  color: #000000;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-top: 4px; /* Espaço reduzido acima do botão */
}

.btn-login:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 15px rgba(255, 102, 0, 0.3);
}

.auth-footer {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  font-size: 12px;
  margin-bottom: 20px;
}

.link-forgot, .link-register {
  color: #ff6600;
  text-decoration: none;
  font-weight: 600;
}

.social-login-container {
  width: 100%;
  display: flex;
  justify-content: center;
}

/* Customização do Botão Google para ser Full Width e sem fundo branco */
.google-btn-custom {
  width: 100% !important;
}

:deep(.S67oY) { /* Seletor interno do Google Sign-In */
  background-color: rgba(255, 255, 255, 0.05) !important;
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
  color: #ffffff !important;
  width: 100% !important;
  border-radius: 8px !important;
}

.auth-right {
  position: relative;
  overflow: hidden;
}

.bg-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: url('@/assets/img/bg_auth.jpg');
  background-size: cover;
  background-position: center;
}

.overlay-gradient {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, rgba(21, 27, 39, 0.7) 0%, rgba(34, 45, 63, 0.3) 100%);
}

@media (max-width: 1024px) {
  .auth-container { grid-template-columns: 1fr; }
  .auth-right { display: none; }
}

/* =====================================================================
   v2010_138 - Layout fixo (imagem estática), formulário rolável sem barra
   e botão Voltar
   =====================================================================
   A tela ocupa exatamente a altura da janela: a imagem do lado direito fica
   estática (mesmo tamanho no Login e no Cadastro) e, se o formulário for mais
   alto que a janela, ele rola dentro do painel esquerdo, sem barra de rolagem
   visível (roda do mouse, toque e teclado continuam funcionando). A roda do
   mouse sobre a imagem também rola o formulário. */
.auth-container {
  position: relative;
  height: 100vh;
  height: 100dvh;
  min-height: 0;
  overflow: hidden;
  grid-template-rows: minmax(0, 1fr);
}

.auth-left {
  min-height: 0;
  align-items: flex-start;
  justify-content: flex-start;
  padding: 72px 40px 40px;
  overflow-x: hidden;
  overflow-y: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.auth-left::-webkit-scrollbar {
  display: none;
  width: 0;
  height: 0;
}

/* margin:auto centraliza quando cabe e deixa o topo alcançável quando não cabe */
.auth-wrapper {
  margin: auto;
  flex-shrink: 0;
}

.auth-right {
  height: 100%;
  min-height: 0;
}

.btn-back {
  position: absolute;
  top: 20px;
  left: 20px;
  z-index: 10;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 9px 16px 9px 12px;
  border-radius: 999px;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  line-height: 1;
  cursor: pointer;
  color: #e6e9f0;
  background: rgba(29, 38, 54, 0.72);
  border: 1px solid rgba(255, 102, 0, 0.35);
  backdrop-filter: blur(10px);
  transition: background 0.2s ease, border-color 0.2s ease, color 0.2s ease, transform 0.2s ease;
}

.btn-back svg {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}

.btn-back:hover {
  color: #ffffff;
  background: rgba(255, 102, 0, 0.18);
  border-color: rgba(255, 102, 0, 0.7);
  transform: translateX(-2px);
}

.btn-back:focus-visible {
  outline: 2px solid #ff6600;
  outline-offset: 2px;
}

@media (max-width: 1024px) {
  /* Coluna única, sem imagem: a página volta a rolar normalmente */
  .auth-container {
    height: auto;
    min-height: 100vh;
    overflow: visible;
    grid-template-rows: none;
  }
  .auth-left {
    overflow: visible;
    padding: 72px 20px 32px;
  }
}

/* =====================================================================
   v2010_139 - Ícones SVG nos campos e preenchimento automático no tema
   ===================================================================== */
.input-icon {
  display: flex;
  align-items: center;
  top: 50%;
  transform: translateY(-50%);
  transition: color 0.2s ease;
}

.toggle-password {
  display: flex;
  align-items: center;
  border-radius: 6px;
  transition: color 0.2s ease;
}

.toggle-password:hover {
  color: #ff6600;
}

.toggle-password:focus-visible {
  outline: 2px solid #ff6600;
  outline-offset: 1px;
}

.input-wrapper:focus-within .input-icon {
  color: #ff6600;
}

.alert-icon {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
}

/* autoprefixer: off */
/* (autoprefixer desligado neste bloco: ele removeria as regras :-webkit-autofill,
   que são as que o Chrome realmente usa.)
   O navegador pinta campos preenchidos automaticamente com um fundo claro
   próprio (que não pode ser trocado com background). O truque abaixo cobre esse
   fundo com a cor do campo do tema e mantém a cor do texto; a transição enorme
   impede a troca de cor animada. Seletores separados de propósito: se um
   navegador não conhece um deles, o outro continua valendo. */
.form-input:-webkit-autofill,
.form-input:-webkit-autofill:hover,
.form-input:-webkit-autofill:focus,
.form-input:-webkit-autofill:active {
  -webkit-box-shadow: 0 0 0 1000px #1f2a3b inset !important;
  box-shadow: 0 0 0 1000px #1f2a3b inset !important;
  -webkit-text-fill-color: #ffffff !important;
  caret-color: #ffffff;
  border-color: rgba(255, 102, 0, 0.25);
  transition: background-color 600000s 0s, color 600000s 0s;
}

.form-input:-webkit-autofill:focus {
  border-color: rgba(255, 102, 0, 0.6);
}

.form-input:autofill {
  box-shadow: 0 0 0 1000px #1f2a3b inset !important;
  -webkit-text-fill-color: #ffffff !important;
  caret-color: #ffffff;
}
/* autoprefixer: on */

/* ---------------------------------------------------------------------
   Tema Claro (v2010_138)
   -----------------------------------------------------------------------
   Paleta do main-light.css. Laranja de texto escurecido (#c24e00) para ter
   contraste legível sobre fundo branco; a imagem do lado direito NÃO recebe
   mais véu claro: fica igual à do tema escuro.
--------------------------------------------------------------------- */
.auth-container.theme-light .auth-left {
  background: linear-gradient(135deg, #fff7f1 0%, #f3f6fb 60%, #ffffff 100%);
}

.auth-container.theme-light .auth-left::before {
  background:
    radial-gradient(circle at 15% 20%, rgba(255, 102, 0, 0.08) 0%, transparent 45%),
    radial-gradient(circle at 85% 85%, rgba(28, 36, 51, 0.05) 0%, transparent 50%);
}

.auth-container.theme-light .auth-wrapper {
  background: #ffffff;
  backdrop-filter: none;
  border: 1px solid rgba(255, 102, 0, 0.22);
  box-shadow: 0 10px 40px rgba(28, 36, 51, 0.12);
}

.auth-container.theme-light .brand-subtitle {
  color: #5b677c;
}

.auth-container.theme-light .form-label {
  color: #3d4757;
}

.auth-container.theme-light .form-input {
  background: #ffffff;
  border: 1px solid #d5dbe5;
  color: #101214;
}

.auth-container.theme-light .form-input:hover {
  border-color: rgba(255, 102, 0, 0.45);
}

.auth-container.theme-light .form-input::placeholder {
  color: #8a95a8;
}

.auth-container.theme-light .form-input:focus {
  border-color: #ff6600;
  box-shadow: 0 0 0 3px rgba(255, 102, 0, 0.18);
}

.auth-container.theme-light .input-icon,
.auth-container.theme-light .toggle-password {
  color: #6b788f;
}

.auth-container.theme-light .toggle-password:hover {
  color: #c24e00;
}

.auth-container.theme-light .btn-login {
  background: linear-gradient(135deg, #f26a0f 0%, #d95400 100%);
  border: 1px solid rgba(217, 84, 0, 0.6);
  color: #ffffff;
}

.auth-container.theme-light .btn-login:hover:not(:disabled) {
  box-shadow: 0 6px 18px rgba(217, 84, 0, 0.35);
}

.auth-container.theme-light .link-forgot,
.auth-container.theme-light .link-register {
  color: #c24e00;
}

.auth-container.theme-light .alert-error {
  background: #fff1f0 !important;
  border: 1px solid #f3b4ac !important;
  color: #9b1c13 !important;
}

.auth-container.theme-light :deep(.S67oY) {
  background-color: #ffffff !important;
  border: 1px solid #d5dbe5 !important;
  color: #101214 !important;
}

.auth-container.theme-light .btn-back {
  color: #4a5568;
  background: rgba(255, 255, 255, 0.92);
  border-color: rgba(255, 102, 0, 0.35);
  box-shadow: 0 2px 8px rgba(28, 36, 51, 0.08);
}

.auth-container.theme-light .btn-back:hover {
  color: #c24e00;
  background: #fff4ec;
  border-color: rgba(217, 84, 0, 0.6);
}

/* Tema claro: preenchimento automático com a mesma aparência dos campos */
.auth-container.theme-light .input-wrapper:focus-within .input-icon {
  color: #d95400;
}

/* autoprefixer: off */
.auth-container.theme-light .form-input:-webkit-autofill,
.auth-container.theme-light .form-input:-webkit-autofill:hover,
.auth-container.theme-light .form-input:-webkit-autofill:focus,
.auth-container.theme-light .form-input:-webkit-autofill:active {
  -webkit-box-shadow: 0 0 0 1000px #ffffff inset !important;
  box-shadow: 0 0 0 1000px #ffffff inset !important;
  -webkit-text-fill-color: #101214 !important;
  caret-color: #101214;
  border-color: #d5dbe5;
}

.auth-container.theme-light .form-input:-webkit-autofill:focus {
  border-color: #ff6600;
}

.auth-container.theme-light .form-input:autofill {
  box-shadow: 0 0 0 1000px #ffffff inset !important;
  -webkit-text-fill-color: #101214 !important;
  caret-color: #101214;
}
/* autoprefixer: on */

.auth-container.theme-light .alert-icon {
  color: #9b1c13;
}
</style>
